/* AuthCallback — handles the return from Emergent-managed Google Auth.
   Emergent redirects the browser to <origin>/app#session_id=XXX after the
   user grants consent. This screen synchronously detects the fragment,
   exchanges the id for an ORIO session cookie (POST /auth/google/session),
   then hard-navigates to /app.

   REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
   THIS BREAKS THE AUTH.
*/
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, PawPrint } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-mount + hash reactivity.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("Sessione Google non trovata. Riprova.");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        await api.post("/auth/google/session", { session_id: sessionId });
        // Clean the fragment so refresh/back doesn't re-trigger exchange
        try {
          window.history.replaceState({}, "", window.location.pathname + window.location.search);
        } catch {}
        await refresh();
        toast.success("Bentornato su ORIO");
        navigate("/app", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Login Google non riuscito. Riprova.";
        setError(msg);
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white flex flex-col items-center justify-center gap-6 px-6">
      <div className="h-16 w-16 rounded-3xl flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                 boxShadow: "0 12px 32px -8px rgba(255,122,26,0.55)" }}>
        <PawPrint className="h-8 w-8 text-white" />
      </div>
      {!error ? (
        <>
          <div className="inline-flex items-center gap-2 text-white/85">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Sto completando l'accesso…</span>
          </div>
          <div className="text-[11px] text-white/40 tracking-wide uppercase">
            Google · ORIO Auth
          </div>
        </>
      ) : (
        <div className="max-w-sm text-center space-y-4">
          <div className="text-sm text-red-300">{error}</div>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="px-5 py-2.5 rounded-full text-sm font-semibold text-white"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}
          >
            Torna al login
          </button>
        </div>
      )}
    </div>
  );
}
